/**
 * Holds the bytes a session was parsed from, between parsing it in the browser
 * and saving the session on the server.
 *
 * A browser upload parses entirely client-side, so the server only ever saw the
 * derived session. Keeping the source lets a later parser fix be replayed against
 * a session that already exists, and lets a bug report be reproduced from stored
 * data rather than by asking the rider to find the original file again.
 *
 * Deliberately module state rather than React state: nothing renders from it, and
 * putting multi-megabyte buffers in a context value would re-render every consumer
 * each time a file is added.
 */
export interface RawTelemetrySource {
  fileName: string
  contentType: string
  content: Blob
}

/** Matches RawTelemetryArchive.MaxFileBytes on the server. */
export const MAX_RAW_TELEMETRY_BYTES = 100 * 1024 * 1024

const sources = new Map<string, RawTelemetrySource>()

/**
 * Associate a source with the client-side session id. Oversized sources are
 * dropped here rather than at save time, so the decision is made once and the
 * bytes are not held in memory for a session that could never store them.
 */
export const rememberRawTelemetry = (
  sessionId: string,
  source: RawTelemetrySource
): void => {
  if (source.content.size <= 0) return
  if (source.content.size > MAX_RAW_TELEMETRY_BYTES) {
    console.warn(
      `Not keeping raw telemetry for ${source.fileName}: ${source.content.size} bytes exceeds the archive limit.`
    )
    return
  }
  sources.set(sessionId, source)
}

/** Retrieve and release the source — it is uploaded at most once. */
export const takeRawTelemetry = (
  sessionId: string
): RawTelemetrySource | undefined => {
  const source = sources.get(sessionId)
  sources.delete(sessionId)
  return source
}

export const forgetRawTelemetry = (sessionId: string): void => {
  sources.delete(sessionId)
}
